import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Button } from 'react-bootstrap';

// TODO: Consider adding thumbnail strip for large galleries
// TODO: Add image zoom functionality in lightbox mode

/**
 * Optimized photo carousel with:
 * - Image preloading for smooth transitions
 * - Keyboard navigation (arrow keys, escape)
 * - Touch/swipe support for mobile
 * - Lightbox/fullscreen mode
 * - Lazy loading for performance
 * - Smooth CSS transitions
 */
const SimpleCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const containerRef = useRef(null);
  const lightboxRef = useRef(null);

  // Minimum swipe distance for navigation (in pixels)
  const minSwipeDistance = 50;

  // ============================================
  // All useCallback hooks MUST be defined first
  // ============================================

  // Navigation functions
  const goToNext = useCallback(() => {
    if (!images || images.length === 0) return;

    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );

    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [images]);

  const goToPrev = useCallback(() => {
    if (!images || images.length === 0) return;

    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );

    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [images]);

  const goToIndex = useCallback(
    (index) => {
      if (!images || index < 0 || index >= images.length) return;

      setIsTransitioning(true);
      setCurrentIndex(index);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    },
    [images]
  );

  // Lightbox functions
  const openLightbox = useCallback(() => {
    console.log('[SimpleCarousel] Opening lightbox');
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    console.log('[SimpleCarousel] Closing lightbox');
    setIsLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  // ============================================
  // useMemo hooks
  // ============================================

  // Preload adjacent images for smooth transitions
  const imagesToPreload = useMemo(() => {
    if (!images || images.length === 0) return [];

    const indices = new Set();
    // Current image
    indices.add(currentIndex);
    // Next image
    indices.add((currentIndex + 1) % images.length);
    // Previous image
    indices.add((currentIndex - 1 + images.length) % images.length);
    // Two ahead for smoother experience
    indices.add((currentIndex + 2) % images.length);

    return Array.from(indices);
  }, [currentIndex, images]);

  // ============================================
  // useEffect hooks
  // ============================================

  // DEBUG: Log component state changes
  useEffect(() => {
    console.log('[SimpleCarousel] State:', {
      currentIndex,
      isLightboxOpen,
      loadedImages: loadedImages.size,
      totalImages: images?.length || 0,
    });
  }, [currentIndex, isLightboxOpen, loadedImages, images]);

  // Preload images
  useEffect(() => {
    if (!images || images.length === 0) return;

    imagesToPreload.forEach((index) => {
      if (!loadedImages.has(index)) {
        const img = new Image();
        img.onload = () => {
          console.log('[SimpleCarousel] Preloaded image:', index);
          setLoadedImages((prev) => new Set([...prev, index]));
        };
        img.src = images[index].url;
      }
    });
  }, [imagesToPreload, images, loadedImages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle keys when carousel is focused or lightbox is open
      if (!containerRef.current?.contains(document.activeElement) && !isLightboxOpen) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'Escape':
          if (isLightboxOpen) {
            e.preventDefault();
            closeLightbox();
          }
          break;
        case 'Enter':
        case ' ':
          if (!isLightboxOpen && document.activeElement === containerRef.current) {
            e.preventDefault();
            openLightbox();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrev, isLightboxOpen, closeLightbox, openLightbox]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // ============================================
  // Touch handlers for swipe support
  // ============================================

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      console.log('[SimpleCarousel] Swipe left detected');
      goToNext();
    } else if (isRightSwipe) {
      console.log('[SimpleCarousel] Swipe right detected');
      goToPrev();
    }
  };

  // ============================================
  // Render
  // ============================================

  // Early return for empty images
  if (!images || images.length === 0) {
    return (
      <div className="simple-carousel text-center p-5 bg-light rounded">
        <i className="bi bi-images" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
        <h3 className="mt-3">No images to display</h3>
        <p className="text-muted">
          Images will appear here once they are added to the gallery.
        </p>
      </div>
    );
  }

  const currentImage = images[currentIndex];
  const isCurrentImageLoaded = loadedImages.has(currentIndex);

  return (
    <>
      {/* Main Carousel */}
      <div
        ref={containerRef}
        className="simple-carousel"
        tabIndex={0}
        role="region"
        aria-label="Image gallery"
        aria-roledescription="carousel"
      >
        <div
          className="image-container"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={openLightbox}
          style={{ cursor: 'zoom-in' }}
        >
          {/* Loading skeleton */}
          {!isCurrentImageLoaded && (
            <div className="image-skeleton">
              <div className="skeleton-shimmer" />
            </div>
          )}

          {/* Main image */}
          <img
            src={currentImage.url}
            alt={currentImage.name || `Project Image ${currentIndex + 1}`}
            className={`simple-carousel-image ${isTransitioning ? 'transitioning' : ''} ${isCurrentImageLoaded ? 'loaded' : 'loading'}`}
            loading="lazy"
            draggable={false}
          />

          {/* Navigation buttons */}
          <Button
            variant="light"
            className="nav-button prev-button"
            onClick={(e) => {
              e.stopPropagation();
              goToPrev();
            }}
            aria-label="Previous image"
          >
            <i className="bi bi-chevron-left"></i>
          </Button>

          <Button
            variant="light"
            className="nav-button next-button"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            aria-label="Next image"
          >
            <i className="bi bi-chevron-right"></i>
          </Button>

          {/* Fullscreen button */}
          <Button
            variant="light"
            className="control-button fullscreen-button"
            onClick={(e) => {
              e.stopPropagation();
              openLightbox();
            }}
            aria-label="View fullscreen"
          >
            <i className="bi bi-arrows-fullscreen"></i>
          </Button>

          {/* Image counter */}
          <div className="image-counter">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Dots navigation */}
          <div className="carousel-indicators" role="tablist">
            {images.map((_, idx) => (
              <button
                key={idx}
                className={`carousel-indicator ${idx === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  goToIndex(idx);
                }}
                role="tab"
                aria-selected={idx === currentIndex}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Keyboard hint */}
        <div className="keyboard-hint text-center mt-2 text-muted small">
          <i className="bi bi-keyboard me-1"></i>
          Use arrow keys to navigate • Click image for fullscreen
        </div>
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div
          ref={lightboxRef}
          className="gallery-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button
              className="lightbox-close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <i className="bi bi-x-lg"></i>
            </button>

            {/* Navigation */}
            <button
              className="lightbox-nav lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              aria-label="Previous image"
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            <button
              className="lightbox-nav lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Next image"
            >
              <i className="bi bi-chevron-right"></i>
            </button>

            {/* Image */}
            <img
              src={currentImage.url}
              alt={currentImage.name || `Project Image ${currentIndex + 1}`}
              className={`lightbox-image ${isTransitioning ? 'transitioning' : ''}`}
              draggable={false}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            />

            {/* Counter */}
            <div className="lightbox-footer">
              <span className="lightbox-counter">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimpleCarousel;
